import { PDFPage, PDFFont, degrees, rgb } from 'pdf-lib';

/**
 * A piece of text the user has placed on a page.
 *
 * Position is stored as fractions (0..1) of the *displayed* page (i.e. the page
 * as shown in the editor, honoring its rotation), measured from the top-left
 * corner. Font size is in PDF points. Keeping positions normalized means they
 * survive reordering and merging without any pixel/scale bookkeeping.
 */
export interface TextAnnotation {
  id: string;
  text: string;
  xFrac: number; // 0..1 from the left edge of the displayed page
  yFrac: number; // 0..1 from the top edge of the displayed page
  fontSize: number; // PDF points
}

/**
 * A filled rectangle ("white-out" box). Position and size are fractions of the
 * displayed page; color is a hex string. Used to cover content the user wants to
 * hide.
 */
export interface RectAnnotation {
  id: string;
  xFrac: number; // top-left
  yFrac: number;
  wFrac: number; // width as a fraction of the displayed page width
  hFrac: number; // height as a fraction of the displayed page height
  color: string; // hex, e.g. '#FFFF00'
}

// All edits applied to a single page. Rectangles are drawn first (behind), then
// text on top, so you can label over a white-out box.
export interface PageEdits {
  texts: TextAnnotation[];
  rects: RectAnnotation[];
}

export const emptyEdits = (): PageEdits => ({ texts: [], rects: [] });

export const DEFAULT_FONT_SIZE = 16;
export const MIN_FONT_SIZE = 6;
export const MAX_FONT_SIZE = 200;

// Basic palette for the box tool. Includes white and yellow; intentionally no
// black (the tool is for whiting/covering things out, not blacking them in).
export const BOX_COLORS = [
  '#FFFFFF', // white
  '#FFFF00', // yellow
  '#FF0000', // red
  '#FF9800', // orange
  '#4CAF50', // green
  '#2196F3', // blue
  '#9C27B0', // purple
  '#BDBDBD', // gray
];
export const DEFAULT_BOX_COLOR = '#FFFFFF';

const hexToRgb = (hex: string): { r: number; g: number; b: number } => {
  const value = parseInt(hex.replace('#', ''), 16);
  return {
    r: ((value >> 16) & 0xff) / 255,
    g: ((value >> 8) & 0xff) / 255,
    b: (value & 0xff) / 255,
  };
};

/**
 * Rotate all edits to follow a 90° clockwise page rotation, so text and boxes
 * stay glued to the same spot. A displayed point (x, y) maps to (1 - y, x); a
 * rectangle's top-left and width/height transform accordingly.
 */
export const rotateEdits90 = (edits: PageEdits): PageEdits => ({
  texts: edits.texts.map((a) => ({ ...a, xFrac: 1 - a.yFrac, yFrac: a.xFrac })),
  rects: edits.rects.map((r) => ({
    ...r,
    xFrac: 1 - r.yFrac - r.hFrac,
    yFrac: r.xFrac,
    wFrac: r.hFrac,
    hFrac: r.wFrac,
  })),
});

/**
 * Draw all edits onto a 2D canvas already rendered with the page's rotation
 * (used for the grid thumbnails). Positions map directly in displayed space.
 */
export const drawEditsOnCanvas = (
  ctx: CanvasRenderingContext2D,
  edits: PageEdits,
  canvasWidth: number,
  canvasHeight: number,
  pageWidthPt: number
): void => {
  for (const r of edits.rects) {
    ctx.fillStyle = r.color;
    ctx.fillRect(r.xFrac * canvasWidth, r.yFrac * canvasHeight, r.wFrac * canvasWidth, r.hFrac * canvasHeight);
  }
  if (edits.texts.length) {
    const scale = canvasWidth / pageWidthPt; // canvas px per PDF point
    ctx.textBaseline = 'alphabetic';
    for (const ann of edits.texts) {
      if (!ann.text) continue;
      const fontPx = ann.fontSize * scale;
      ctx.font = `${fontPx}px Helvetica, Arial, sans-serif`;
      ctx.fillStyle = '#000000';
      ctx.fillText(ann.text, ann.xFrac * canvasWidth, ann.yFrac * canvasHeight + fontPx);
    }
  }
};

// Map a point from displayed space (bottom-left origin, y up) to the page's
// unrotated MediaBox coordinates, given the page rotation theta.
const mapDisplayedToUnrotated = (
  Dx: number,
  Dy: number,
  theta: number,
  Wu: number,
  Hu: number
): { u: number; v: number } => {
  switch (theta) {
    case 90:
      return { u: Wu - Dy, v: Dx };
    case 180:
      return { u: Wu - Dx, v: Hu - Dy };
    case 270:
      return { u: Dy, v: Hu - Dx };
    default:
      return { u: Dx, v: Dy };
  }
};

/**
 * Draw all edits onto a pdf-lib page for export.
 *
 * Edits are stored in displayed space (top-left origin), but pdf-lib draws in the
 * page's unrotated MediaBox space (bottom-left origin) before the viewer applies
 * the page's /Rotate. So we map anchors back through the rotation. 90° rotations
 * keep axis-aligned rectangles axis-aligned, so boxes need no rotate option;
 * text is pre-rotated so the viewer's rotation leaves it upright.
 */
export const drawEditsOnPdfPage = (page: PDFPage, edits: PageEdits, font: PDFFont): void => {
  const { width: Wu, height: Hu } = page.getSize(); // unrotated MediaBox
  const theta = ((page.getRotation().angle % 360) + 360) % 360;
  const rotated = theta === 90 || theta === 270;
  const Wd = rotated ? Hu : Wu; // displayed width
  const Hd = rotated ? Wu : Hu; // displayed height

  // Rectangles first (behind the text).
  for (const r of edits.rects) {
    const px = r.xFrac * Wd;
    const py = r.yFrac * Hd;
    const wPts = r.wFrac * Wd;
    const hPts = r.hFrac * Hd;
    // Two opposite corners in displayed bottom-left-origin space.
    const c1 = mapDisplayedToUnrotated(px, Hd - (py + hPts), theta, Wu, Hu);
    const c2 = mapDisplayedToUnrotated(px + wPts, Hd - py, theta, Wu, Hu);
    const { r: rr, g: gg, b: bb } = hexToRgb(r.color);
    page.drawRectangle({
      x: Math.min(c1.u, c2.u),
      y: Math.min(c1.v, c2.v),
      width: Math.abs(c2.u - c1.u),
      height: Math.abs(c2.v - c1.v),
      color: rgb(rr, gg, bb),
    });
  }

  // Text on top.
  for (const ann of edits.texts) {
    if (!ann.text) continue;
    const Dx = ann.xFrac * Wd;
    const Dy = Hd - (ann.yFrac * Hd + ann.fontSize);
    const { u, v } = mapDisplayedToUnrotated(Dx, Dy, theta, Wu, Hu);
    page.drawText(ann.text, {
      x: u,
      y: v,
      size: ann.fontSize,
      font,
      color: rgb(0, 0, 0),
      rotate: degrees(theta),
    });
  }
};
